
/* =========================================================
   WHEELHUT — DYNAMIC CANDYRACK TYRE BUNDLE MESSAGE

   Standard: 15% off tyres
   TyreBundle20: 20% off tyres
   TyreBundle25: 25% off tyres

   Automatically refreshes when the cart changes.
   ========================================================= */

(function () {
  'use strict';

  const ROOT = window.Shopify?.routes?.root || '/';

  let currentDiscount = null;
  let lastCartSignature = null;
  let refreshTimer = null;
  let messageUpdateScheduled = false;
  let refreshInProgress = false;
  let refreshAgain = false;

  const productCache = new Map();

  /* ---------------------------------------------------------
     Retrieve product tags
     --------------------------------------------------------- */

  async function getProductTags(handle) {
    if (!handle) return [];

    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    const response = await fetch(
      ROOT + 'products/' + encodeURIComponent(handle) + '.js',
      {
        credentials: 'same-origin'
      }
    );

    if (!response.ok) {
      throw new Error('Unable to load product');
    }

    const product = await response.json();

    const tags = Array.isArray(product.tags)
      ? product.tags
      : String(product.tags || '').split(',');

    const normalizedTags = tags.map(function (tag) {
      return String(tag).trim().toLowerCase();
    });

    productCache.set(handle, normalizedTags);

    return normalizedTags;
  }

  /* ---------------------------------------------------------
     Determine tyre bundle discount
     --------------------------------------------------------- */

  async function getBundleDiscount(cart) {
    const items = cart.items || [];

    if (!items.length) {
      return null;
    }

    let discount = 15;
    let wheelFound = false;

    for (const item of items) {
      const productName = (
        (item.product_type || '') + ' ' +
        (item.product_title || '')
      ).toLowerCase();

      if (!productName.includes('wheel')) {
        continue;
      }

      wheelFound = true;

      const tags = await getProductTags(item.handle);

      if (tags.includes('tyrebundle25')) {
        return 25;
      }

      if (tags.includes('tyrebundle20')) {
        discount = 20;
      }
    }

    return wheelFound ? discount : null;
  }

  /* ---------------------------------------------------------
     Update promotional messages
     --------------------------------------------------------- */

  function updateTyreMessages() {
    const container = document.querySelector(
      '#candyrack-slider-cart'
    );

    if (!container) return;

    container.querySelectorAll(
      '.candyrack-slider-cart-offer-item-wrapper'
    ).forEach(function (offer) {

      const title = offer.querySelector(
        '.candyrack-slider-cart-offer-item-header'
      );

      const priceWrapper = offer.querySelector(
        '.candyrack-slider-cart-offer-price-wrapper'
      );

      if (!title || !priceWrapper) return;

      const productTitle = title.textContent || '';
      const productUrl = title.getAttribute('href') || '';

      const isTyre = /tyre|tire/i.test(
        productTitle + ' ' + productUrl
      );

      const existingMessage = offer.querySelector(
        '.ms7-candyrack-promo-message'
      );

      if (!isTyre || currentDiscount === null) {
        if (existingMessage) existingMessage.remove();
        return;
      }

      if (
        existingMessage &&
        existingMessage.dataset.discount ===
          String(currentDiscount)
      ) {
        return;
      }

      if (existingMessage) {
        existingMessage.remove();
      }

      const message = document.createElement('div');

      message.className = 'ms7-candyrack-promo-message';
      message.dataset.discount = String(currentDiscount);

      message.innerHTML = `
        <div class="ms7-candyrack-saving">
          <span>Add this tyre and </span>
          <span class="ms7-candyrack-saving-value">
            save ${currentDiscount}%
          </span>
        </div>
        <div class="ms7-candyrack-fitting">
          Tyre/s will come fitted at no extra cost
        </div>
      `;

      priceWrapper.insertAdjacentElement(
        'afterend',
        message
      );
    });
  }

  /* ---------------------------------------------------------
     Read current Shopify cart
     --------------------------------------------------------- */

  async function refreshDiscount() {
    if (refreshInProgress) {
      refreshAgain = true;
      return;
    }

    refreshInProgress = true;

    try {
      const response = await fetch(ROOT + 'cart.js', {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error('Unable to load cart');
      }

      const cart = await response.json();

      const signature = (cart.items || []).map(function (item) {
        return item.key + ':' + item.quantity;
      }).join('|');

      if (signature !== lastCartSignature) {
        lastCartSignature = signature;
        currentDiscount = await getBundleDiscount(cart);
      }

      updateTyreMessages();

    } catch (error) {
      console.warn(
        'WheelHut tyre bundle message:',
        error
      );

      currentDiscount = null;
      lastCartSignature = null;
      updateTyreMessages();

    } finally {
      refreshInProgress = false;

      if (refreshAgain) {
        refreshAgain = false;
        scheduleCartRefresh();
      }
    }
  }

  /* ---------------------------------------------------------
     Schedule cart refresh
     --------------------------------------------------------- */

  function scheduleCartRefresh() {
    clearTimeout(refreshTimer);

    refreshTimer = setTimeout(function () {
      refreshDiscount();
    }, 300);
  }

  /* ---------------------------------------------------------
     Schedule message update
     --------------------------------------------------------- */

  function scheduleMessageUpdate() {
    if (messageUpdateScheduled) return;

    messageUpdateScheduled = true;

    requestAnimationFrame(function () {
      messageUpdateScheduled = false;
      updateTyreMessages();
    });
  }

  /* ---------------------------------------------------------
     Detect cart changes
     --------------------------------------------------------- */

  function start() {
    refreshDiscount();

    const observer = new MutationObserver(function (mutations) {
      let cartChanged = false;
      let offerChanged = false;

      mutations.forEach(function (mutation) {
        const target = mutation.target;

        if (
          target &&
          target.nodeType === 1 &&
          target.closest
        ) {
          if (
            target.closest(
              '.ms7-candyrack-promo-message'
            )
          ) {
            return;
          }

          if (
            target.closest(
              '#CartDrawer-CartItems'
            )
          ) {
            cartChanged = true;
          }

          if (
            target.closest(
              '#candyrack-slider-cart'
            )
          ) {
            offerChanged = true;
          }
        }

        Array.from(mutation.addedNodes).forEach(function (node) {
          if (node.nodeType !== 1) return;

          if (
            node.matches?.(
              '#CartDrawer-CartItems'
            ) ||
            node.querySelector?.(
              '#CartDrawer-CartItems'
            )
          ) {
            cartChanged = true;
          }

          if (
            node.matches?.(
              '#candyrack-slider-cart'
            ) ||
            node.querySelector?.(
              '#candyrack-slider-cart'
            )
          ) {
            offerChanged = true;
          }
        });
      });

      if (cartChanged) {
        scheduleCartRefresh();
      }

      if (offerChanged) {
        scheduleMessageUpdate();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener(
      'cart:updated',
      scheduleCartRefresh
    );

    document.addEventListener(
      'cart:refresh',
      scheduleCartRefresh
    );

    document.addEventListener(
      'cart:change',
      scheduleCartRefresh
    );

    window.addEventListener(
      'focus',
      scheduleCartRefresh
    );

    document.addEventListener(
      'visibilitychange',
      function () {
        if (!document.hidden) {
          scheduleCartRefresh();
        }
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start
    );
  } else {
    start();
  }

})();
